import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { progress } from '@/api/endpoints';
import { useSession } from '@/store/session';
import { Icon } from '@/components/ui/Icon';
import {
  Button, Empty, ErrorNote, Loading, Meter, Panel, ScreenBody, ScreenHeader, Stat, StatGrid,
} from '@/components/ui/primitives';

type Achievement = Awaited<ReturnType<typeof progress.achievements>>['results'][number];

export function Achievements({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<Achievement[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = useSession((s) => s.refresh);

  const load = useCallback(() => {
    void progress.achievements().then((r) => setRows(r.results));
  }, []);
  useEffect(load, [load]);

  async function claim(key: string) {
    setBusy(key); setError(null);
    try {
      await progress.claimAchievement(key);
      load();
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not claim that.');
    } finally { setBusy(null); }
  }

  if (!rows) return <Loading />;

  const unlocked = rows.filter((r) => r.unlocked).length;

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="trophy" size={24} />} title="Achievements" onBack={onBack} />
      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel title="PROGRESS">
        <StatGrid>
          <Stat k="Unlocked" v={`${unlocked} / ${rows.length}`} tone="ok" />
          <Stat k="Claimable" v={rows.filter((r) => r.claimable).length} tone="coin" />
        </StatGrid>
        <div className="mt-2.5">
          <Meter pct={rows.length ? (unlocked / rows.length) * 100 : 0} tone="gold" label="Achievements unlocked" />
        </div>
      </Panel>

      <Panel className="mt-3">
        {rows.length === 0 ? <Empty>Nothing tracked yet.</Empty> : rows.map((a) => {
          const pct = a.threshold > 0 ? (a.progress / a.threshold) * 100 : 0;
          return (
            <div key={a.key}
                 className={`border-t border-[var(--hairline)] py-3 first:border-t-0
                             ${a.unlocked ? '' : 'opacity-60'}`}>
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border
                                 border-[var(--hairline)] bg-black/35 text-lg" aria-hidden>
                  {a.unlocked ? <Icon name="trophy" size={20} className="text-coin" /> : <Icon name="lock" size={20} className="text-ink-dim" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold">{a.name}</p>
                  <p className="text-[11px] text-ink-dim">{a.description}</p>
                  {!a.unlocked && (
                    <>
                      <div className="mt-1.5"><Meter pct={pct} tone="gold" label={`${a.name} progress`} /></div>
                      <p className="mt-1 text-[10px] text-ink-dim tabular">
                        {a.progress.toLocaleString('en-US')} / {a.threshold.toLocaleString('en-US')}
                      </p>
                    </>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="flex items-center justify-end gap-1 text-xs font-bold text-coin tabular">
                    <Icon name="coin" size={13} />
                    {a.reward_coins.toLocaleString('en-US')}
                  </p>
                  {Number(a.reward_kyat) > 0 && (
                    <p className="flex items-center justify-end gap-1 text-xs font-bold text-kyat-glow tabular">
                      <Icon name="kyat" size={13} />
                      {a.reward_kyat}
                    </p>
                  )}
                </div>
              </div>
              {a.claimable && (
                <Button variant="primary" className="mt-2 w-full" busy={busy === a.key}
                        onClick={() => void claim(a.key)}>
                  Claim reward
                </Button>
              )}
            </div>
          );
        })}
      </Panel>
    </ScreenBody>
  );
}
